/**
 * The six objectives and what each one governs (VOX-270 — Feature 2).
 *
 * ⚠️ This is NOT a backend gap. The PRD calls the objective catalogue "a
 * product definition, not editable by UI" and says adding an objective must not
 * require a redesign. So it lives in `domain/` and survives the endpoints
 * landing, exactly like the planning math.
 *
 * The objective is picked first because it decides everything downstream: the
 * funnel stages, which dispositions exist and which of them count as a
 * conversion, the metrics, and the results table's schema. A sales campaign and
 * a collections campaign are not the same thing measured differently — they are
 * different measurements.
 *
 * Every string here is an i18n KEY, not a label: the catalogue has no idea what
 * language it will be read in.
 */

import type {
  CampaignObjectiveId,
  CampaignOutcome,
  OutcomeGroup,
  Pacing,
  RetryInterval,
} from '../types';
import { foldOutcomeLabel } from './foldLabel';
import { matchHeaders } from './resultsHeaders';

export { foldOutcomeLabel } from './foldLabel';

/** One value of a `select` column: a stable key, and the i18n key of the label the table shows. */
export interface ObjectiveOption {
  key: string;
  label: string;
}

/** A column the objective adds to its results table (PRD Appendix A). */
export interface ObjectiveColumn {
  key: string;
  /** i18n key for the column header. */
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'formula';
  /**
   * The values a `select` column is born with. Dynamic Tables refuses a select
   * column with no options (400), so a select without them cannot be created.
   * The call's results are read into one of these (Voice
   * `campaignCallExtraction.service.ts`), which is what makes them filterable.
   */
  options?: ObjectiveOption[];
}

/** A select's options, with their labels under `objectives.<objective>.option.<column>.<key>`. */
const optionsOf = (objective: string, column: string, keys: string[]): ObjectiveOption[] =>
  keys.map((key) => ({ key, label: `objectives.${objective}.option.${column}.${key}` }));

export interface CampaignObjective {
  id: CampaignObjectiveId;
  /** i18n keys. */
  name: string;
  description: string;
  /** What "converted" is called for this objective. */
  conversionLabel: string;
  /**
   * The funnel, in order, after the shared `Audience → Contacted` opening every
   * objective starts from. The last stage IS the conversion.
   *
   * The first rung is the objective's own "talked to the right party"
   * (identity verified, survey started, churn reason diagnosed, delivered) —
   * then its middle rungs.
   */
  stages: string[];
  /** Dispositions this objective adds on top of the shared ones. */
  dispositions: string[];
  /** Columns its results table carries beyond the three shared blocks. */
  columns: ObjectiveColumn[];
  /**
   * Whether a declared result can later be verified against reality
   * (promise → payment, appointment → attended, retained → still active).
   *
   * False for Survey and Notification, and not by omission: their conversion is
   * verified in the call itself, so there is nothing to check afterwards.
   */
  hasVerificationBoard: boolean;
}

export const CAMPAIGN_OBJECTIVES: CampaignObjective[] = [
  {
    id: 'collections',
    name: 'objective.collections',
    description: 'objectives.collections.description',
    conversionLabel: 'objectives.collections.conversion',
    stages: [
      'objectives.shared.stage.identityVerified',
      'objectives.collections.stage.acknowledges',
      'objectives.collections.stage.negotiation',
      'objectives.collections.stage.promise',
    ],
    dispositions: [
      'objectives.collections.disposition.requestsNegotiation',
      'objectives.collections.disposition.disputesBalance',
      'objectives.shared.disposition.escalate',
    ],
    columns: [
      { key: 'committedAmount', label: 'objectives.collections.column.amount', type: 'number' },
      { key: 'promiseDate', label: 'objectives.collections.column.promiseDate', type: 'date' },
      {
        key: 'agreedMethod',
        label: 'objectives.collections.column.method',
        type: 'select',
        options: optionsOf('collections', 'method', [
          'transfer',
          'card',
          'cash',
          'directDebit',
          'store',
        ]),
      },
      { key: 'remainingBalance', label: 'objectives.collections.column.balance', type: 'formula' },
      {
        key: 'noPromiseReason',
        label: 'objectives.collections.column.noPromise',
        type: 'select',
        options: optionsOf('collections', 'noPromise', [
          'noFunds',
          'disputes',
          'alreadyPaid',
          'callLater',
          'refuses',
          'other',
        ]),
      },
    ],
    hasVerificationBoard: true,
  },
  {
    id: 'sales',
    name: 'objective.sales',
    description: 'objectives.sales.description',
    conversionLabel: 'objectives.sales.conversion',
    stages: [
      'objectives.shared.stage.identityVerified',
      'objectives.sales.stage.qualified',
      'objectives.sales.stage.proposal',
      'objectives.sales.stage.negotiation',
      'objectives.sales.stage.lead',
    ],
    dispositions: [
      'objectives.sales.disposition.moreInfo',
      'objectives.shared.disposition.escalate',
    ],
    columns: [
      { key: 'product', label: 'objectives.sales.column.product', type: 'select' },
      { key: 'amount', label: 'objectives.sales.column.amount', type: 'number' },
      {
        key: 'recurrence',
        label: 'objectives.sales.column.recurrence',
        type: 'select',
        options: optionsOf('sales', 'recurrence', [
          'oneOff',
          'monthly',
          'quarterly',
          'biannual',
          'annual',
        ]),
      },
      { key: 'annualisedValue', label: 'objectives.sales.column.annualised', type: 'formula' },
      {
        key: 'mainObjection',
        label: 'objectives.sales.column.objection',
        type: 'select',
        options: optionsOf('sales', 'objection', [
          'price',
          'timing',
          'competitor',
          'noNeed',
          'trust',
          'decisionMaker',
          'other',
        ]),
      },
      {
        key: 'nextStep',
        label: 'objectives.sales.column.nextStep',
        type: 'select',
        options: optionsOf('sales', 'nextStep', [
          'sendInfo',
          'followUpCall',
          'meeting',
          'closeSale',
          'none',
        ]),
      },
      { key: 'followUpDate', label: 'objectives.sales.column.followUp', type: 'date' },
    ],
    hasVerificationBoard: false,
  },
  {
    id: 'scheduling',
    name: 'objective.scheduling',
    description: 'objectives.scheduling.description',
    conversionLabel: 'objectives.scheduling.conversion',
    // The shortest funnel of the six: identified, then booked or not.
    stages: ['objectives.shared.stage.identityVerified', 'objectives.scheduling.stage.confirmed'],
    dispositions: [
      'objectives.scheduling.disposition.confirmed',
      'objectives.scheduling.disposition.rescheduled',
      'objectives.scheduling.disposition.cancelled',
      'objectives.scheduling.disposition.callback',
      'objectives.shared.disposition.escalate',
    ],
    columns: [
      { key: 'appointmentAt', label: 'objectives.scheduling.column.appointment', type: 'date' },
      {
        key: 'modality',
        label: 'objectives.scheduling.column.modality',
        type: 'select',
        options: optionsOf('scheduling', 'modality', ['inPerson', 'video', 'phone']),
      },
      { key: 'venue', label: 'objectives.scheduling.column.venue', type: 'text' },
      {
        key: 'cancelReason',
        label: 'objectives.scheduling.column.cancelReason',
        type: 'select',
        options: optionsOf('scheduling', 'cancelReason', [
          'noTime',
          'notInterested',
          'alreadyAttended',
          'reschedule',
          'other',
        ]),
      },
    ],
    hasVerificationBoard: true,
  },
  {
    id: 'survey',
    name: 'objective.survey',
    description: 'objectives.survey.description',
    conversionLabel: 'objectives.survey.conversion',
    stages: [
      'objectives.survey.stage.started',
      'objectives.survey.stage.inProgress',
      'objectives.survey.stage.completed',
    ],
    dispositions: [],
    columns: [
      { key: 'totalQuestions', label: 'objectives.survey.column.total', type: 'number' },
      { key: 'answered', label: 'objectives.survey.column.answered', type: 'number' },
      { key: 'progress', label: 'objectives.survey.column.progress', type: 'formula' },
      { key: 'score', label: 'objectives.survey.column.score', type: 'number' },
      // Computed from the score (0–6 / 7–8 / 9–10), never asked: a classification, so a select.
      {
        key: 'npsCategory',
        label: 'objectives.survey.column.nps',
        type: 'select',
        options: optionsOf('survey', 'nps', ['promoter', 'passive', 'detractor']),
      },
      { key: 'comment', label: 'objectives.survey.column.comment', type: 'text' },
    ],
    // Its conversion is verified in the call itself.
    hasVerificationBoard: false,
  },
  {
    id: 'retention',
    name: 'objective.retention',
    description: 'objectives.retention.description',
    conversionLabel: 'objectives.retention.conversion',
    stages: [
      'objectives.retention.stage.diagnosis',
      'objectives.retention.stage.offer',
      'objectives.retention.stage.response',
      'objectives.retention.stage.retained',
    ],
    dispositions: [
      'objectives.retention.disposition.retained',
      'objectives.retention.disposition.confirmsChurn',
      'objectives.retention.disposition.uncooperative',
      'objectives.shared.disposition.escalate',
    ],
    columns: [
      {
        key: 'churnReason',
        label: 'objectives.retention.column.reason',
        type: 'select',
        options: optionsOf('retention', 'reason', [
          'price',
          'service',
          'competitor',
          'noUse',
          'moving',
          'other',
        ]),
      },
      {
        key: 'offerPresented',
        label: 'objectives.retention.column.offer',
        type: 'select',
        options: optionsOf('retention', 'offer', [
          'discount',
          'upgrade',
          'downgrade',
          'freeMonths',
          'none',
        ]),
      },
      { key: 'discountPercent', label: 'objectives.retention.column.discount', type: 'number' },
      { key: 'retainedValue', label: 'objectives.retention.column.value', type: 'formula' },
      { key: 'churnDate', label: 'objectives.retention.column.churnDate', type: 'date' },
    ],
    hasVerificationBoard: true,
  },
  {
    id: 'notification',
    name: 'objective.notification',
    description: 'objectives.notification.description',
    conversionLabel: 'objectives.notification.conversion',
    // Delivered first and identified after: the notice reaching a line is the
    // right-party rung, confirming WHO heard it comes next.
    stages: [
      'objectives.notification.stage.reached',
      'objectives.notification.stage.verified',
      'objectives.notification.stage.delivered',
    ],
    dispositions: [
      'objectives.notification.disposition.confirmedByHolder',
      'objectives.shared.disposition.escalate',
    ],
    // No columns of its own: the shared blocks say everything a notification has
    // to record.
    columns: [],
    hasVerificationBoard: false,
  },
];

const BY_ID = new Map(CAMPAIGN_OBJECTIVES.map((o) => [o.id, o]));

export function objectiveById(id: CampaignObjectiveId): CampaignObjective | undefined {
  return BY_ID.get(id);
}

/**
 * The columns a results table gets for an objective.
 *
 * Exported separately because Feature 5 previews the schema BEFORE creating the
 * table, and it must preview the same list the table is then born with.
 */
export function columnsForObjective(id: CampaignObjectiveId): ObjectiveColumn[] {
  return objectiveById(id)?.columns ?? [];
}

/**
 * Whether an existing results table can receive this objective's campaign.
 *
 * A table missing any of the objective's own columns cannot record what the
 * skill will write, so the PRD blocks the choice with an explanation rather
 * than letting the campaign run and drop the data.
 */
export function missingColumnsFor(
  id: CampaignObjectiveId,
  tableColumnKeys: string[],
  /**
   * The header a column is CREATED with (its translated label). A real table
   * names its columns "Monto comprometido", not `committedAmount`: without this
   * a table the wizard just created would read as incompatible with itself.
   * The header in every other language counts too (`resultsHeaders.ts`): a
   * table created in Spanish is not incompatible because the app is in English.
   */
  headerOf?: (column: ObjectiveColumn) => string,
): ObjectiveColumn[] {
  const present = new Set(tableColumnKeys);
  const byHeader = headerOf ? columnHeaders(id, tableColumnKeys, headerOf) : new Map();
  return columnsForObjective(id).filter((c) => !present.has(c.key) && !byHeader.has(c.key));
}

/**
 * The objective's columns found in the table by header, by catalogue key.
 * `headerOf` is the user's language; the others are tried too.
 */
function columnHeaders(
  id: CampaignObjectiveId,
  tableColumnKeys: string[] | undefined,
  headerOf: (column: ObjectiveColumn) => string,
) {
  const columns = columnsForObjective(id);
  const byLabel = new Map(columns.map((c) => [c.label, headerOf(c)]));
  return matchHeaders(
    tableColumnKeys,
    columns.map((c) => [c.key, c.label] as const),
    (key) => byLabel.get(key) ?? key,
  );
}

/** A Dynamic Tables field type a results column can be created with. */
export type ResultsFieldType = 'text' | 'number' | 'date' | 'select';

/**
 * The type a results column is CREATED with.
 *
 * ⚠️ `formula` goes as `number`. Dynamic Tables formulas are arithmetic over
 * `prop("columnId")` references, and most of the catalogue's formulas cannot
 * be written that way: "Saldo restante" needs the original balance, which lives
 * in the source table; "Categoría NPS" is a classification, not a sum. The
 * skill writes the value already computed.
 *
 * ⚠️ A `select` with no `options` goes as `text`. Dynamic Tables refuses a
 * select with no options. "Producto" (sales) is the one left so on purpose:
 * what a business sells is its own, not the catalogue's.
 */
export function fieldTypeFor(column: Pick<ObjectiveColumn, 'type' | 'options'>): ResultsFieldType {
  if (column.type === 'formula') return 'number';
  if (column.type === 'select' && !column.options?.length) return 'text';
  return column.type;
}

/** One column of a results table, ready to create. */
export interface ResultsColumnSpec {
  /** The header, already in the user's language. */
  name: string;
  fieldType: ResultsFieldType;
  options?: string[];
}

/**
 * A column a results table is created with: one of the objective's
 * (`ResultsColumnSpec`), or one copied from the contacts table, which keeps
 * whatever type it has there.
 */
export interface TableColumnSpec {
  name: string;
  fieldType: string;
  /** Option labels, for a select. */
  options?: string[];
}

/**
 * The columns a new results table is created with, in the catalogue's order —
 * the same list, and the same types, the step's schema preview shows.
 */
export function resultsColumnsFor(
  id: CampaignObjectiveId,
  headerOf: (column: ObjectiveColumn) => string,
  optionLabel: (option: ObjectiveOption) => string = (option) => option.label,
): ResultsColumnSpec[] {
  return columnsForObjective(id).map((column) => ({
    name: headerOf(column),
    fieldType: fieldTypeFor(column),
    ...(column.options ? { options: column.options.map(optionLabel) } : {}),
  }));
}

/** What Voice needs to fill one objective column from a call (Voice `ObjectiveColumnInput`). */
export interface ObjectiveColumnSchema {
  header: string;
  type: ResultsFieldType;
  options?: { key: string; label: string }[];
}

type Translate = (key: string) => string;

/**
 * The objective's columns the results table really has, by catalogue key —
 * what the campaign tells Voice to fill from each call. A column the table
 * lacks is left out, like the shared ones.
 *
 * Found by header in any language (`resultsHeaders.ts`), and sent with the
 * header AS THE TABLE HAS IT and a select's options in the language that
 * header was found in: Voice validates each cell against the options the
 * column was created with, not against the user's language.
 */
export function objectiveColumnsIn(
  id: CampaignObjectiveId,
  tableColumnKeys: string[] | undefined,
  t: Translate,
): Record<string, ObjectiveColumnSchema> {
  const columns = columnsForObjective(id);
  const found = matchHeaders(
    tableColumnKeys,
    columns.map((c) => [c.key, c.label] as const),
    t,
  );
  return Object.fromEntries(
    columns.flatMap((column) => {
      const match = found.get(column.key);
      if (!match) return [];
      const schema: ObjectiveColumnSchema = {
        header: match.header,
        type: fieldTypeFor(column),
        ...(column.options
          ? { options: column.options.map((o) => ({ key: o.key, label: match.t(o.label) })) }
          : {}),
      };
      return [[column.key, schema] as const];
    }),
  );
}

/**
 * Which group each catalogue disposition belongs to (VOX-315).
 *
 * ⚠️ PROPOSED MAPPING, pending product confirmation (open question in
 * `openspec/changes/vox-315-campaign-custom-results/design.md`).
 * Every key must be a catalogue disposition and every catalogue disposition
 * must be here: `objectives.test.ts` enforces both, and `groupOfDisposition`
 * throws on a missing one instead of defaulting. `no-contact` is never here:
 * it comes from the technical call dispositions.
 */
export const DISPOSITION_GROUPS: Readonly<Record<string, OutcomeGroup>> = {
  'objectives.collections.disposition.requestsNegotiation': 'rejection',
  'objectives.collections.disposition.disputesBalance': 'rejection',
  'objectives.sales.disposition.moreInfo': 'rejection',
  'objectives.scheduling.disposition.confirmed': 'success',
  'objectives.scheduling.disposition.rescheduled': 'success',
  'objectives.scheduling.disposition.cancelled': 'rejection',
  'objectives.scheduling.disposition.callback': 'rejection',
  'objectives.retention.disposition.retained': 'success',
  'objectives.retention.disposition.confirmsChurn': 'rejection',
  'objectives.retention.disposition.uncooperative': 'rejection',
  // The holder confirming receipt is the notification's win.
  'objectives.notification.disposition.confirmedByHolder': 'success',
  'objectives.shared.disposition.escalate': 'rejection',
};

function groupOfDisposition(key: string): OutcomeGroup {
  const group = DISPOSITION_GROUPS[key];
  if (!group) throw new Error(`Disposition "${key}" has no outcome group in DISPOSITION_GROUPS`);
  return group;
}

/** Outcome label limit (`contract.yml`: label `maxLength`). */
export const MAX_OUTCOME_LABEL_LENGTH = 100;

/**
 * The business outcomes an objective can record, in the user's language: its
 * conversion (group `success`) first, then each disposition with its group.
 *
 * Keys are the catalogue's i18n keys — `objectives.<id>.conversion` and
 * `objectives.<id>.disposition.<name>` — stable across languages and edits of
 * the labels. Labels are trimmed and capped at `MAX_OUTCOME_LABEL_LENGTH`. Never empty: the conversion is always there.
 */
export function objectiveOutcomes(
  objective: CampaignObjectiveId | CampaignObjective,
  t: Translate,
): CampaignOutcome[] {
  const def = typeof objective === 'string' ? objectiveById(objective) : objective;
  if (!def) return [];
  const make = (key: string, group: OutcomeGroup): CampaignOutcome => ({
    key,
    label: t(key).trim().slice(0, MAX_OUTCOME_LABEL_LENGTH).trim(),
    group,
    conversation: true,
    custom: false,
  });
  // Scheduling's "confirmed" and retention's "retained" read exactly like
  // their conversion: they ARE the conversion, listed once.
  return dedupeOutcomesByLabel([
    make(def.conversionLabel, 'success'),
    ...def.dispositions.map((key) => make(key, groupOfDisposition(key))),
  ]);
}

/**
 * Keeps the first outcome of each label, compared with `foldOutcomeLabel`:
 * the backend rejects two outcomes whose labels fold the same.
 */
export function dedupeOutcomesByLabel(outcomes: readonly CampaignOutcome[]): CampaignOutcome[] {
  const seen = new Set<string>();
  return outcomes.filter(({ label }) => {
    const id = foldOutcomeLabel(label);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/** How many outcomes a campaign may hold (`contract.yml`: `outcomes.maxItems`; Voice `MAX_OUTCOMES`). */
export const MAX_OUTCOMES = 40;

/**
 * The outcomes an edit sends (VOX-315): the ones the campaign already has,
 * IDENTICAL and in their order, plus the objective's that are missing.
 *
 * The wizard only knows the objective's outcomes, so sending its list would
 * overwrite the campaign's custom ones — and a started campaign must keep every
 * stored outcome unchanged (Voice answers 409 `results.outcomes` otherwise).
 * An objective outcome counts as present when its key is stored (the same one
 * labelled in another language) or its label folds like a stored one (Voice
 * refuses two such labels). Never more than `MAX_OUTCOMES`: what is stored is
 * kept whole, the objective's fill what is left.
 */
export function mergeOutcomes(
  stored: readonly CampaignOutcome[],
  objective: readonly CampaignOutcome[],
): CampaignOutcome[] {
  const keys = new Set(stored.map(({ key }) => key));
  const labels = new Set(stored.map(({ label }) => foldOutcomeLabel(label)));
  const merged = [...stored];
  for (const outcome of objective) {
    if (merged.length >= MAX_OUTCOMES) break;
    const label = foldOutcomeLabel(outcome.label);
    if (keys.has(outcome.key) || labels.has(label)) continue;
    keys.add(outcome.key);
    labels.add(label);
    merged.push(outcome);
  }
  return merged;
}

/** The objective's funnel, in order and in the user's language: what the "Etapa" column picks from. */
export function objectiveStageLabels(id: CampaignObjectiveId, t: Translate): string[] {
  return (objectiveById(id)?.stages ?? []).map((stage) => t(stage));
}

/* ─── Templates ──────────────────────────────────────────────────────────── */

/**
 * What a campaign of this objective is born with.
 *
 * Collections and Notification look nothing alike — one insists, the other
 * expires — so one global default made everybody reconfigure the same thing
 * every time. The boundary is deliberate: the template decides WHEN and HOW
 * MANY TIMES a contact is called, never HOW MANY a day. The daily cap and the
 * capacity belong to the account, not to the objective.
 */
export interface ObjectiveTemplate {
  // No skill per objective: a fixed skill id (`invoice`, `lead-qual`,
  // `renewal`) would be a mock skill, and in production
  // every agent has its own skills with their own ids — nothing maps one onto
  // the other, so there is no skill for the template to carry.
  maxRetries: number;
  /**
   * The wait between retries this objective starts with, in MINUTES. What
   * reaches the campaign is
   * `retryIntervalFor(retryWaitMinutes)`: see there why 240 and 1440 do not
   * survive as such.
   */
  retryWaitMinutes: number;
  /** The call window. It becomes ONE slot. */
  window: { from: string; to: string };
  /** Active ISO weekdays (1 = Monday). */
  days: number[];
}

const TEMPLATES: Record<CampaignObjectiveId, ObjectiveTemplate> = {
  // Collections insists, and close together: the balance won't pay itself and
  // the negotiation window closes with the cut-off.
  collections: {
    maxRetries: 2,
    retryWaitMinutes: 30,
    window: { from: '09:00', to: '19:00' },
    days: [1, 2, 3, 4, 5, 6],
  },
  sales: {
    maxRetries: 2,
    retryWaitMinutes: 30,
    window: { from: '09:00', to: '19:00' },
    days: [1, 2, 3, 4, 5],
  },
  // An appointment is confirmed or lost: the retries fit in the same day.
  scheduling: {
    maxRetries: 2,
    retryWaitMinutes: 30,
    window: { from: '09:00', to: '20:00' },
    days: [1, 2, 3, 4, 5, 6],
  },
  // Spaced out: insisting with a survey annoys more than it lifts. And it is
  // answered better late, once people are off work.
  survey: {
    maxRetries: 2,
    retryWaitMinutes: 1440,
    window: { from: '11:00', to: '20:00' },
    days: [1, 2, 3, 4, 5, 6],
  },
  retention: {
    maxRetries: 2,
    retryWaitMinutes: 240,
    window: { from: '09:00', to: '19:00' },
    days: [1, 2, 3, 4, 5],
  },
  // One short retry: a notice that arrives three days late is noise.
  notification: {
    maxRetries: 1,
    retryWaitMinutes: 30,
    window: { from: '09:00', to: '20:00' },
    days: [1, 2, 3, 4, 5],
  },
};

export function templateFor(id: CampaignObjectiveId): ObjectiveTemplate {
  return TEMPLATES[id];
}

/** The retry waits the backend accepts, in minutes. */
const RETRY_INTERVAL_MINUTES: Record<RetryInterval, number> = { '30m': 30, '1h': 60, '2h': 120 };

/**
 * The accepted retry wait closest to a template's minutes.
 *
 * ⚠️ The templates ask for 4h and 1d (Retention, Survey), but Voice only accepts
 * `30m | 1h | 2h` (`campaign.service.ts` validates `retryInterval` against that
 * list and refuses anything else with a 400). Sending them would make the
 * launch fail, so they land on the closest accepted value — `2h`, the longest
 * the backend has. Widening it is a Voice change, not a frontend one.
 */
export function retryIntervalFor(minutes: number): RetryInterval {
  const options = Object.entries(RETRY_INTERVAL_MINUTES) as [RetryInterval, number][];
  return options.reduce((best, option) =>
    Math.abs(option[1] - minutes) < Math.abs(best[1] - minutes) ? option : best,
  )[0];
}

/**
 * The pace with the objective's template applied.
 *
 * Applied WHOLE on each change of objective, overwriting the previous days,
 * window and retries on purpose: the wizard is linear — Execution can't be
 * reached without passing through here — so changing the objective again IS
 * restarting the plan, not nuancing it. The cap, the time zone and the
 * per-date exceptions are not the objective's and are kept.
 */
export function pacingWithTemplate(prev: Pacing, id: CampaignObjectiveId): Pacing {
  const template = templateFor(id);
  return {
    ...prev,
    days: [...template.days],
    slots: [{ id: 'slot-1', from: template.window.from, to: template.window.to }],
    maxRetries: template.maxRetries,
    retryInterval: retryIntervalFor(template.retryWaitMinutes),
  };
}
